import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { FileText, Receipt, Key, Plus, Bell, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalDashboard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [licenses, setLicenses] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/quotes`).then(r => setQuotes(r.data)).catch(() => {});
    axios.get(`${API}/api/invoices`).then(r => setInvoices(r.data)).catch(() => {});
    axios.get(`${API}/api/licenses`).then(r => setLicenses(r.data)).catch(() => {});
    axios.get(`${API}/api/notifications`).then(r => setNotifications(r.data)).catch(() => {});
    axios.get(`${API}/api/notifications/unread-count`).then(r => setUnreadCount(r.data.count)).catch(() => {});
  }, []);

  const markAllRead = async () => {
    await axios.put(`${API}/api/notifications/read-all`);
    setUnreadCount(0);
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const stats = [
    { icon: FileText, label: t('portal.quotes'), value: quotes.length, color: 'var(--primary)', path: '/portal/quotes' },
    { icon: Receipt, label: t('portal.invoices'), value: invoices.length, color: 'var(--secondary)', path: '/portal/invoices' },
    { icon: Key, label: t('portal.licenses'), value: licenses.length, color: 'var(--primary-dark)', path: '/portal/licenses' },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar type="portal" active="dashboard" />
      <main data-testid="portal-dashboard" style={{ flex: 1, background: 'var(--bg-portal)', padding: '32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)' }}>
                {t('portal.welcome')}, {user?.name}
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: 15, marginTop: 4 }}>{user?.company}</p>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {/* Notification Bell */}
              <div style={{ position: 'relative' }}>
                <button data-testid="notification-bell" onClick={() => setShowNotifs(!showNotifs)}
                  style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid var(--border)', cursor: 'pointer', position: 'relative' }}>
                  <Bell size={20} style={{ color: 'var(--text-secondary)' }} />
                  {unreadCount > 0 && (
                    <span style={{ position: 'absolute', top: -4, right: -4, width: 20, height: 20, background: 'var(--secondary)', color: 'white', borderRadius: '50%', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unreadCount}</span>
                  )}
                </button>

                {showNotifs && (
                  <div data-testid="notifications-panel" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, width: 360, maxHeight: 400, overflowY: 'auto', background: 'white', border: '1px solid var(--border)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50 }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700 }}>Notifications</h3>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {unreadCount > 0 && <button onClick={markAllRead} style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>Mark all read</button>}
                        <button onClick={() => setShowNotifs(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
                      </div>
                    </div>
                    {notifications.length === 0 ? (
                      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No notifications</div>
                    ) : notifications.map(n => (
                      <div key={n._id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--bg-section)', background: n.read ? 'white' : 'rgba(13,71,161,0.04)' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.read ? 'transparent' : 'var(--secondary)', marginTop: 6, flexShrink: 0 }} />
                          <div>
                            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{n.title}</p>
                            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{n.message}</p>
                            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/services" data-testid="portal-new-quote-btn" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'var(--primary)', color: 'white', padding: '12px 24px',
                fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                <Plus size={18} /> {t('portal.new_quote')}
              </Link>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 32 }}>
            {stats.map(s => {
              const Icon = s.icon;
              return (
                <Link to={s.path} key={s.label} style={{
                  background: 'white', border: '1px solid var(--border)', padding: 24,
                  display: 'flex', alignItems: 'center', gap: 16, textDecoration: 'none',
                }}>
                  <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${s.color}10` }}>
                    <Icon size={24} style={{ color: s.color }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>{s.value}</p>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{s.label}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Recent quotes */}
          <div style={{ background: 'white', border: '1px solid var(--border)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 600 }}>{t('portal.quotes')}</h2>
            </div>
            {quotes.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('portal.no_quotes')}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {[t('portal.product'), t('portal.status'), t('portal.date')].map(h => (
                        <th key={h} style={{ padding: '12px 24px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.slice(0, 5).map((q: any) => (
                      <tr key={q._id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '14px 24px', fontSize: 14, fontWeight: 500 }}>{q.product_name}</td>
                        <td style={{ padding: '14px 24px' }}>
                          <span style={{ padding: '4px 10px', fontSize: 12, fontWeight: 600, background: q.status === 'approved' ? 'rgba(34,197,94,0.12)' : q.status === 'rejected' ? 'rgba(239,68,68,0.12)' : 'rgba(251,191,36,0.12)', color: q.status === 'approved' ? '#16A34A' : q.status === 'rejected' ? '#DC2626' : '#D97706', textTransform: 'uppercase' }}>
                            {t(`status.${q.status}`) || q.status}
                          </span>
                        </td>
                        <td style={{ padding: '14px 24px', fontSize: 13, color: 'var(--text-muted)' }}>{new Date(q.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
